import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {PaperChatReceivingBubbleComponent} from "../paper-chat-receiving-bubble/paper-chat-receiving-bubble.component";
import {PaperChatSendComponent} from "../paper-chat-send/paper-chat-send.component";
import {PaperChatSendingBubbleComponent} from "../paper-chat-sending-bubble/paper-chat-sending-bubble.component";
import {Observable, Subscription} from 'rxjs';
import {selectConnectedForProject, selectFilterType, selectMessagesForProject} from '../../service/paper-chat.selector';
import {selectPapersByProjectId} from '../../../research/services/research-paper/research-paper.selectors';
import {Store} from '@ngrx/store';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {PaperChatWebsocketDTO} from '../../dto/paper-chat';
import {ResearchPaperDTO} from '../../../research/dto/research-paper';
import {connectToProject, disconnect} from '../../service/paper-chat.actions';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {map, switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-paper-chat',
  imports: [
    PaperChatReceivingBubbleComponent,
    PaperChatSendComponent,
    PaperChatSendingBubbleComponent
  ],
  templateUrl: './paper-chat.component.html',
  styleUrl: './paper-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaperChatComponent implements OnDestroy, AfterViewInit {
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private readonly store: Store = inject(Store);

  projectId = input.required<number>();
  paperId = input<number>();

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  readonly messages = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => this.store.select(selectMessagesForProject(projectId)))
    ),
    {initialValue: [] as PaperChatWebsocketDTO[]}
  );
  readonly connected = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => this.store.select(selectConnectedForProject(projectId)))
    ),
    {initialValue: false}
  );
  readonly papers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => this.store.select(selectPapersByProjectId(projectId)))
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );
  readonly isSmallScreen = toSignal(
    this.breakpointObserver.observe([Breakpoints.Handset]).pipe(map(result => result.matches)),
    {initialValue: false}
  );
  readonly filterType = this.store.selectSignal(selectFilterType());

  private autoScrollEnabled = true;

  private scrollEventSubscription?: Subscription;

  constructor() {
    effect((onCleanup) => {
      const projectId = this.projectId();
      this.store.dispatch(connectToProject({projectId, paperId: this.paperId()}));
      onCleanup(() => this.store.dispatch(disconnect({projectId})));
    });

    effect(() => {
      this.messages();
      queueMicrotask(() => this.scrollToBottom());
    });
  }

  ngAfterViewInit() {
    this.scrollEventSubscription = this.monitorScrollEvents();
  }

  ngOnDestroy(): void {
    if (this.scrollEventSubscription) {
      this.scrollEventSubscription.unsubscribe();
    }
  }

  private monitorScrollEvents(): Subscription {
    if (!this.messagesContainer) {
      console.error('messagesContainer is not defined');
      return new Subscription();
    }
    const container = this.messagesContainer.nativeElement;

    return new Observable<void>((observer) => {
      const onScroll = () => {
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 1; // Add a small threshold
        this.autoScrollEnabled = isAtBottom;
      };
      container.addEventListener('scroll', onScroll);

      return () => container.removeEventListener('scroll', onScroll);
    }).subscribe();
  }

  private scrollToBottom(): void {
    if (!this.autoScrollEnabled || !this.messagesContainer) {
      return;
    }
    this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
  }
}
